/**
 * useOnboardingStore.js — v213.5 (C-A-OB-1)
 *
 * In-memory state machine for the 7-screen patient onboarding flow.
 *
 * Flow order (0-indexed) — 2FA added in-flow v228.1.0:
 *   0 Welcome (basics)
 *   1 Security (2FA setup — grants the live session for new signups)
 *   2 Identity + ID         (required)
 *   3 Insurance             (required, or self-pay)
 *   4 Auto-fill             (records → confirm; seeds steps 5–7)
 *   5 Allergies             (confirm/edit autofilled, or add)
 *   6 Medications           (confirm/edit autofilled, or add)
 *   7 Optional bundle       (medical / family / lifestyle)
 *
 * Security runs second: Basics is in-memory (no PHI), then 2FA grants the
 * session before any PHI is captured (ID/insurance/clinical). Auto-fill
 * runs after the card-scan steps (never autofilled) and before the clinical
 * sections it seeds, so those stay confirm-only.
 *
 * Then a Done overlay (not a counted step — auto-transitions to /home).
 *
 * --------------------------------------------------------------------------
 * IMPORTANT — data shapes must match Profile screens exactly.
 *
 * The onboarding flow writes the same JSONB columns the Profile screens
 * edit later. If the shapes drift, Profile will misread or wipe what
 * onboarding wrote. Any change to a Profile section's shape must be
 * mirrored here. Verified against Profile screens at v213.5:
 *
 *   identification : { firstName, lastName, dob, sex, zipCode, email, phone,
 *                      streetAddress?, apt?, city?, state?, idFront?, idBack?, completed }
 *   insurance      : { selfPay, coverageType, provider, providerId, ipa?, ipaId?,
 *                      memberId, groupNumber, cardFront?, cardBack?, completed,
 *                      insurerName, planType, copayPCP?, copaySpecialist?, ... }
 *   allergies      : { medList, foodList, contactList,
 *                      hasMedAllergies, hasFoodAllergies, hasContactAllergies,
 *                      noKnownAllergies, list, completed }
 *                      where each item is { id, name, severity, reaction, custom? }
 *   medications    : { hasMedications, noMedications, list, completed }
 *                      where each item is { name, dosage, frequency, purpose }
 *   medicalHistory : { conditions, surgeries, noConditions, noSurgeries, completed }
 *                      where conditions/surgeries are [{ id, name, year, custom? }]
 *   familyHistory  : { conditions, otherConditions, unknownRelatives, completed }
 *                      where conditions are [{ id, name, mother, father, sibling, grandparent }]
 *   socialLifestyle: { smoking, smokingFrequency, alcohol, alcoholFrequency,
 *                      cannabis, cannabisFrequency, cannabisMethod,
 *                      exercise, occupation, completed }
 *                      smoking/alcohol/cannabis values are 'Yes' | 'No' | 'Former'
 * --------------------------------------------------------------------------
 *
 * Persistence: state is held in memory only. On submit we POST to
 * /api/v1/profile with `onboardingCompleted: true` and clear the store.
 * If the patient closes the app mid-flow, state is lost; on next sign-in
 * `onboarding_completed === false` re-routes them to step 0.
 */

import { create } from 'zustand'
import {
  MEDICAL_CONDITIONS,
  FAMILY_CONDITIONS,
} from '../data/medicalLists'
import { payerKeysForWire, scanPayerPick, PAYER_KEYS } from '../utils/payerPick'
import { policyholderForWire, isoDob } from '../utils/discoverySearch'
import { slimCard as _slimCard } from '../utils/slimCard'
import { isNkaAllergyPhrase, isNkaMedPhrase, normalizeNkaPhrase } from '../utils/noKnownItems'
import { familyRelations } from '../utils/familyRelation'
import { accountContact } from '../utils/welcomeContact'
import { cardPcpFromScan } from '../utils/pcpChoice' // s106 A0: the card's PCP, confirmed on the insurance step

// s95 sign-up rebuild: Security · About you · Agreements. The health-profile screens run
// as a separate, optional sequence after sign-up (OnboardingFlow phase 'profile').
const TOTAL_STEPS = 3

// ★★ TEST 3.01 interim (242.229.0, review 1 minor 2): the Email and Mobile step 1 SENDS. A field
//   OnboardingWelcome shows locked (the account holds a value the step accepts: welcomeContact
//   accountContact) is sent as the ACCOUNT's value, never as identity.email / .phone: those can
//   hold an older value, because OnboardingFlow's resume effect (hydrateFromProfile, from a saved
//   profile or the pre-2FA sessionStorage draft) runs AFTER Welcome's mount effect and puts back
//   what an earlier draft held. An unlocked field (no usable value on the account) sends what the
//   patient typed. `account` is the caller's user || pendingProfile (none: the typed values).
const contactForWire = (identity, account) => {
  const c = accountContact(account)
  return {
    email: c.emailLocked ? c.email : identity.email,
    // PROD-1167: no phone here. The account number (users.phone) is the one number; a profile
    //   save never carries one (utils/profileWire.js), and phone_verify mirrors it into the profile.
  }
}

const initialIdentity = {
  firstName: '',
  lastName: '',
  dob: '',
  sex: '',
  zipCode: '',
  email: '',
  phone: '',
  // Profile shape extras — left blank during onboarding, patient adds later
  streetAddress: '',
  apt: '',
  city: '',
  state: '',
  // ID artifacts from CardScanner
  idFront: null,
  idBack: null,
}

const initialInsurance = {
  selfPay: false,
  coverageType: '', // 'ppo' | 'hmo' | 'epo' | 'medicare' | 'medicaid' | 'self-pay' | 'hsa' | etc.
  provider: '',
  providerId: '',
  memberId: '',
  groupNumber: '',
  // v228.11.8 — subscriber (policyholder) relationship captured at onboarding.
  subscriberRelationship: '',
  subscriberFirstName: '',
  subscriberLastName: '',
  subscriberDOB: '',
  cardFront: null,
  cardBack: null,
  // v228.2.0 — Stedi results captured inline on the Insurance step (Approach A).
  // Held here so the final buildPayload carries them and never clobbers the
  // discovery_result / last_eligibility the Stedi endpoints persisted mid-flow.
  discovery_result: null,
  last_eligibility: null,
}

const initialAllergies = {
  // Items are kept flat in onboarding state ({id, name, type, severity, reaction, source, confirmed}).
  // Split into medList/foodList/contactList at submit time to match Profile shape.
  items: [],
  noKnown: false,
}

const initialMedications = {
  // Items: { id, name, dosage, frequency, purpose, source, confirmed }
  items: [],
  none: false,
}

const initialOptional = {
  // medicalHistory.conditions: [{ id, name, year, custom? }]
  // medicalHistory.surgeries:  [{ id, name, year, custom? }]
  medicalHistory: null,

  // familyHistory.conditions: [{ id, name, mother, father, sibling, grandparent }]
  // familyHistory.otherConditions: [{ id, name, mother, father, sibling, grandparent }]
  familyHistory: null,

  // socialLifestyle: Profile-compatible flat object
  socialLifestyle: null,
}

// ---------- Helpers ----------

// v228.11.6: a captured card carries the image as a base64 data URL; it must not reach
// the profile PUT. P17 step 6 (PROD-237): the reduction moved, verbatim, to
// utils/slimCard.js, so Profile → Insurance / Identification save through the same one.
// It is imported under its old name and the call sites below are unchanged.

/**
 * v228.3.2 — No decoy carrier list. The server resolves the canonical Stedi
 * payer from the card (card payer ID → name fallback). This helper now only
 * handles the no-resolution fallback: keep the raw OCR'd name as a custom entry
 * with no fabricated carrier ID.
 */
function matchCarrier(rawName) {
  if (!rawName) return { name: '', id: '' }
  return { name: rawName, id: 'custom' }
}

/**
 * Match an extracted condition string against MEDICAL_CONDITIONS by name
 * (loose case-insensitive). Returns the canonical { id, name } if found,
 * else { id: 'custom-{slug}', name: rawName, custom: true }.
 */
function matchMedicalCondition(rawName) {
  if (!rawName) return null
  const lc = rawName.toLowerCase().trim()
  const found = MEDICAL_CONDITIONS.find((c) => c.name.toLowerCase() === lc)
  if (found) return { id: found.id, name: found.name, year: '' }
  // Loose match (substring)
  const loose = MEDICAL_CONDITIONS.find(
    (c) => lc.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(lc)
  )
  if (loose) return { id: loose.id, name: loose.name, year: '' }
  return {
    id: `custom-${lc.replace(/\s+/g, '-')}`,
    name: rawName.trim(),
    year: '',
    custom: true,
  }
}

function matchFamilyCondition(rawName) {
  if (!rawName) return null
  const lc = rawName.toLowerCase().trim()
  const exact = FAMILY_CONDITIONS.find((c) => c.name.toLowerCase() === lc)
  if (exact) return { id: exact.id, name: exact.name }
  const loose = FAMILY_CONDITIONS.find(
    (c) => lc.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(lc)
  )
  if (loose) return { id: loose.id, name: loose.name }
  return { id: `custom-${lc.replace(/\s+/g, '-')}`, name: rawName.trim(), custom: true }
}

/**
 * Map free-text smoking/alcohol/cannabis status from extracted social history
 * into Profile's 'Yes' | 'No' | 'Former' values.
 */
function mapStatus(raw) {
  if (!raw) return ''
  const lc = String(raw).toLowerCase()
  if (lc.includes('never') || lc === 'none' || lc === 'no') return 'No'
  if (lc.includes('former') || lc.includes('quit') || lc.includes('past')) return 'Former'
  if (lc.includes('current') || lc.includes('yes') || lc.includes('daily') || lc.includes('regular') || lc.includes('social')) return 'Yes'
  return ''
}

// ★★ PROD-428 (a)+(f), patient push 3a — A HAND REMOVAL OR A HAND CORRECTION IS REMEMBERED.
//   A later upload re-added what the patient had removed, and a dose the patient corrected
//   (remove the AI line, add the right one) came back as a second line. A section setter
//   called with `{ perItem: true }` records the content key of each item that edit DROPPED
//   (removed, or replaced by an item whose key differs) in `autofillSuppressed`, and
//   applyAutofillResult never re-adds a suppressed key. Keys are content, not ids: the same
//   normalisation the merge uses. Held in memory with the rest of this store.
// ★★★ PROD-473 (patient 242.229.0), the proper fix behind review r2's STOPGAP: a removed ALLERGY
//   is remembered as substance + reaction. The substance alone (3a) blocked a later record's
//   worse reaction to it; the stopgap applied nothing, so a re-read of the same record brought
//   the removal back. Now a removed substance stays removed UNLESS the later record brings a
//   reaction that was not on the removed item, or (Alex, 2026-10-02 Q6) rises to Severe when
//   the removed item was not Severe. See allergyStillRemoved.
// ★ REVIEW r2 (MINOR, S2h): a removed FAMILY condition's key is recorded but NOT applied (it
//   blocked the condition for every relative); an unticked relative still is (MINOR 1).
// ★★ PROD-473 family half (2026-10-09): a removed family condition is remembered PER RELATIVE, as
//   `<condition key>\u0000<relative>` for each relative ticked on it (the same memory as an untick,
//   familyRelatives). A re-read of the same record no longer brings it back; a later record's
//   condition for ANOTHER relative still lands, for that relative only.
// ★★★ REVIEW r1 (M2): ONLY A PER-ITEM REMOVAL OR EDIT RECORDS. The setters used to record on
//   EVERY call, so "No known allergies" (declareNoKnown), "No medications" (declareNone) and
//   the history "None" toggles (toggleMedNone, toggleFamNone) — which clear the whole list —
//   suppressed every substance on it: Penicillin, then a later record with Penicillin
//   ANAPHYLAXIS, and the anaphylaxis was silently dropped under noKnown true. A call without
//   `{ perItem: true }` (those four, documents, hydrate) records nothing.
const SUPPRESS_SECTIONS = ['allergies', 'medications', 'conditions', 'surgeries', 'family']
const histKeyOf = (x) => normalizeNkaPhrase(String((x && x.name) || '').replace(/[-_]+/g, ' '))
const suppressKeyOf = {
  allergies: (a) => normalizeNkaPhrase(a && a.name),
  medications: (m) => [m && m.name, m && m.dosage, m && m.frequency].map(normalizeNkaPhrase).join('\u0000'),
  conditions: histKeyOf,
  surgeries: histKeyOf,
  family: histKeyOf,
}
// ★ review r1 MINOR 1: a relative the patient UNTICKED on a family condition that stays on the
//   list ("Diabetes: not my mother, my grandmother") is remembered as `<condition key>\u0000<relative>`,
//   and mergeRelatives never ticks it again from a later record.
const FAMILY_RELATIVES = ['mother', 'father', 'sibling', 'grandparent']
function untickedRelatives(prevList, nextList) {
  const next = new Map((Array.isArray(nextList) ? nextList : []).map((x) => [histKeyOf(x), x]))
  const out = []
  for (const p of Array.isArray(prevList) ? prevList : []) {
    const n = next.get(histKeyOf(p))
    if (n) for (const rel of FAMILY_RELATIVES) if (p[rel] && !n[rel]) out.push(`${histKeyOf(p)}\u0000${rel}`)
  }
  return out
}
// PROD-473 family half: `<condition key>\u0000<relative>` for each relative ticked on a family
//   condition that this edit REMOVED from the list (the per-item chip, toggleFamCondition).
function removedRelatives(prevList, nextList) {
  const keep = new Set((Array.isArray(nextList) ? nextList : []).map(histKeyOf))
  const out = []
  for (const p of Array.isArray(prevList) ? prevList : []) {
    const k = histKeyOf(p)
    if (k && !keep.has(k)) for (const rel of FAMILY_RELATIVES) if (p[rel]) out.push(`${k}\u0000${rel}`)
  }
  return out
}
function droppedKeys(prevList, nextList, keyOf) {
  const keep = new Set((Array.isArray(nextList) ? nextList : []).map(keyOf))
  return (Array.isArray(prevList) ? prevList : []).map(keyOf).filter((k) => k && !keep.has(k))
}
// PROD-473: `<substance>\u0000<reaction>` for each reaction an allergy carries. A record lists
//   several as "Rash; Hives" or "Rash, Hives", so both ';' and ',' separate them, when a removal
//   is recorded and when a later record is compared: a re-read "Hives, Rash" of a removed
//   "Rash, Hives" says nothing new and stays removed (review 1, minor 1).
const allergyReactionKeys = (a) => {
  const sub = normalizeNkaPhrase(a && a.name)
  return String((a && a.reaction) || '').split(/[;,]/).map(normalizeNkaPhrase).filter(Boolean).map((r) => `${sub}\u0000${r}`)
}
const isSevere = (severity) => normalizeNkaPhrase(severity) === 'severe'
// PROD-1006 (Alex Q59, 2026-10-14): merged reactions to one allergen show the HIGHEST severity,
//   never a lower one. Order: Mild < Moderate < Severe; blank, 'Unknown' or any other value ranks 0
//   and neither outranks nor erases a known one.
const severityRank = (severity) => ({ mild: 1, moderate: 2, severe: 3 })[normalizeNkaPhrase(severity)] || 0
// PROD-473: an allergy the patient removed by hand (`sup`, autofillSuppressed) is NOT re-added by
//   a later record that says nothing new about it — no reaction absent from the removed item,
//   and no rise to Severe (Q6). Anything new brings it back, unconfirmed, as an AI item.
const allergyStillRemoved = (sup, a) => {
  const sub = normalizeNkaPhrase(a && a.name)
  if (!sub || !(sup.allergies || []).includes(sub)) return false
  if (isSevere(a.severity) && !(sup.allergySevere || []).includes(sub)) return false
  const removedReactions = new Set(sup.allergyReactions || [])
  return allergyReactionKeys(a).every((k) => removedReactions.has(k))
}
function withSuppressed(s, nextState, how) {
  if (!(how && how.perItem === true)) return nextState
  const add = {}
  const lists = (st) => ({
    allergies: st.allergies?.items,
    medications: st.medications?.items,
    conditions: st.optional?.medicalHistory?.conditions,
    surgeries: st.optional?.medicalHistory?.surgeries,
    family: [...(st.optional?.familyHistory?.conditions || []), ...(st.optional?.familyHistory?.otherConditions || [])],
  })
  const before = lists(s)
  const after = lists({ ...s, ...nextState })
  for (const sec of SUPPRESS_SECTIONS) {
    const d = droppedKeys(before[sec], after[sec], suppressKeyOf[sec])
    if (d.length) add[sec] = d
  }
  // PROD-473: what each removed allergy said, so a later record can be compared with it
  if (add.allergies) {
    const gone = new Set(add.allergies)
    const removed = (Array.isArray(before.allergies) ? before.allergies : []).filter((a) => gone.has(suppressKeyOf.allergies(a)))
    const reactions = removed.flatMap(allergyReactionKeys)
    const severe = removed.filter((a) => isSevere(a.severity)).map(suppressKeyOf.allergies)
    if (reactions.length) add.allergyReactions = reactions
    if (severe.length) add.allergySevere = severe
  }
  const unticked = [...untickedRelatives(before.family, after.family), ...removedRelatives(before.family, after.family)]
  if (unticked.length) add.familyRelatives = unticked
  if (!Object.keys(add).length) return nextState
  const cur = s.autofillSuppressed || {}
  const merged = { ...cur }
  for (const [sec, keys] of Object.entries(add)) merged[sec] = [...new Set([...(cur[sec] || []), ...keys])]
  return { ...nextState, autofillSuppressed: merged }
}

// ★★★ R30 · PROD-428 (g), D456 (patient P2-2) — PER HABIT, THE NEWER RECORD WINS, STATUS AND DETAILS
//   AS ONE PAIR. Supersedes keep-first for habits: an older "never smoked" outranked a newer
//   "current smoker". "Newer" is the date written in the document (`documentDate`, YYYY-MM-DD);
//   only when a record is undated does upload order decide (the later upload is the newer). A
//   same-date pair: upload order. orella-api c71d3cb's parse-medical-document returns no
//   document date yet, so today every record is undated and the later upload wins.
//   Never mixed across records: a newer DIFFERENT status brings its own details (or none); a
//   newer SAME status refreshes the details it names and keeps the rest. An older record only
//   fills the empty details of the same status. A `locked` habit (typed by hand, or loaded from
//   the profile: no record is known to have supplied it) is never overwritten (R29).
//   Shared by both merge points: this store (onboarding auto-fill) and
//   pages/profile/ImportMedicalRecordsScreen.jsx (profile import).
export function recordDateOf(result) {
  const d = result && typeof result.documentDate === 'string' ? result.documentDate.trim() : ''
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null
}
export const isNewerRecord = (incomingDate, prevDate) => !(incomingDate && prevDate && incomingDate < prevDate)
export function mergeHabitPairs(prev, incoming, units, { newer = {}, locked = {} } = {}) {
  const isEmpty = (v) => v == null || String(v).trim() === ''
  const merged = { ...prev }
  const took = []
  for (const [st, details] of units) {
    const prevSet = !isEmpty(prev[st])
    const inSet = !isEmpty(incoming[st])
    const isNewer = newer[st] !== false
    if (!inSet) {
      if (!prevSet) for (const d of details) if (isEmpty(prev[d]) && !isEmpty(incoming[d])) merged[d] = incoming[d]
      continue
    }
    if (!prevSet) {
      merged[st] = incoming[st]
      for (const d of details) merged[d] = incoming[d] == null ? '' : incoming[d]
      took.push(st)
      continue
    }
    if (locked[st]) continue
    if (incoming[st] === prev[st]) {
      for (const d of details) if (!isEmpty(incoming[d]) && (isNewer || isEmpty(prev[d]))) merged[d] = incoming[d]
      if (isNewer) took.push(st)
      continue
    }
    if (isNewer) {
      merged[st] = incoming[st]
      for (const d of details) merged[d] = isEmpty(incoming[d]) ? '' : incoming[d] // the newer pair, its details or none
      took.push(st)
    }
  }
  return { merged, took }
}
const SOCIAL_HABIT_UNITS = [
  ['smoking', ['smokingFrequency']],
  ['alcohol', ['alcoholFrequency']],
  ['cannabis', ['cannabisFrequency', 'cannabisMethod']],
]

// ---------- Store ----------

export const useOnboardingStore = create((set, get) => ({
  currentStep: 0,
  totalSteps: TOTAL_STEPS,

  // v242.8.0: first-run intro carousel. Shown once before the step machine on a
  // fresh onboarding; flips true on Get Started / Skip so it never re-appears.
  carouselSeen: false,
  setCarouselSeen: (v = true) => set({ carouselSeen: v }),

  // Auto-fill outcome — populated when user uploads on screen 2
  autofillResult: null,
  autofillSkipped: false,
  // TEST 3.03: every record read on the auto-fill step, { id, name }, so the step can
  // show what was already uploaded (also after Back). The file itself is never kept.
  autofillDocuments: [],
  // PROD-428 (a)+(f): content keys the patient removed or corrected by hand, per section
  autofillSuppressed: {},
  // R30 · PROD-428 (g): per habit, the pair a record supplied and that record's date —
  //   { smoking: { status, details: {…}, date } }. A habit not here, or changed since, was typed.
  autofillHabits: {},

  identity: { ...initialIdentity },
  insurance: { ...initialInsurance },
  allergies: { ...initialAllergies },
  medications: { ...initialMedications },
  optional: { ...initialOptional },

  isSubmitting: false,
  submitError: null,

  // PROD-696 (P4): the 'carrier|memberId' (utils/eligibilityLimit.js eligibilityKey) whose paid
  //   eligibility check the insurance step already sent in this run, so the section save in
  //   OnboardingFlow does not pay for the same coverage a second time. Cleared when the check
  //   failed to reach the server (the section save then tries once).
  eligibilityCheckedKey: null,
  setEligibilityCheckedKey: (key) => set({ eligibilityCheckedKey: key || null }),
  // Q-C5 (PROD-691, Alex 2026-10-07): the 'carrier|memberId' the shown insurance.last_eligibility
  //   was checked for (null: not known). At the daily limit the server answers its stored result,
  //   so a check of ANOTHER member ID shows no verdict (utils/eligibilityLimit.js limitedResultFor).
  //   Client-only, never sent.
  eligibilityResultKey: null,
  setEligibilityResultKey: (key) => set({ eligibilityResultKey: key || null }),
  // PROD-713 review B-S1 (2026-10-09): the insurance step's eligibility check while it is out,
  //   { key, put, done } (null: none). The step can leave the screen before the answer (Continue,
  //   then Back): the next mount draws that answer instead of paying for another check, and waits
  //   for its PUT /profile like the step that sent it. Client-only, never sent.
  eligibilityRun: null,
  setEligibilityRun: (run) => set({ eligibilityRun: run || null }),

  // ----- navigation -----
  goToStep: (n) => set({ currentStep: Math.max(0, Math.min(TOTAL_STEPS - 1, n)) }),
  nextStep: () => set((s) => ({ currentStep: Math.min(TOTAL_STEPS - 1, s.currentStep + 1) })),
  prevStep: () => set((s) => ({ currentStep: Math.max(0, s.currentStep - 1) })),

  // ----- section setters -----
  setIdentity: (patch) => set((s) => ({ identity: { ...s.identity, ...patch } })),
  setInsurance: (patch) => set((s) => ({ insurance: { ...s.insurance, ...patch } })),
  // `how`: `{ perItem: true }` from a per-item removal or edit ONLY (see withSuppressed)
  setAllergies: (patch, how) => set((s) => withSuppressed(s, { allergies: { ...s.allergies, ...patch } }, how)),
  setMedications: (patch, how) => set((s) => withSuppressed(s, { medications: { ...s.medications, ...patch } }, how)),
  setOptional: (patch, how) => set((s) => withSuppressed(s, { optional: { ...s.optional, ...patch } }, how)),

  // ----- insurance helpers -----
  /**
   * Apply OCR'd insurance card data. Fuzzy-matches carrier against
   * the known list so the patient sees a recognized provider, not a
   * raw OCR string.
   */
  applyInsuranceScan: (extracted, side = 'cardFront', cardData = null) => {
    if (!extracted) {
      if (cardData) set((s) => ({ insurance: { ...s.insurance, [side]: cardData } }))
      return
    }

    set((s) => {
      const next = {
        insurance: {
          ...s.insurance,
          ...(cardData ? { [side]: cardData } : {}),
          selfPay: false,
          // Prefer the canonical Stedi payer the server resolved from the card.
          // ★ PROD-187 (P12): the scan's payer verdict replaces the WHOLE payer key set, and
          //   only when the scan yields a carrier that differs from the one held
          //   (utils/payerPick.js scanPayerPick). The per-key `extracted.x || s.insurance.x`
          //   merge kept the previous payer's id under every key the scan did not carry.
          ...(scanPayerPick(extracted, s.insurance) || {}),
          memberId: extracted.memberId || s.insurance.memberId,
          groupNumber: extracted.groupNumber || s.insurance.groupNumber,
          coverageType:
            extracted.planType
              ? String(extracted.planType).toLowerCase().replace(/[^a-z]/g, '')
              : s.insurance.coverageType,
          // s106 (canvas "Orella Referrals Screens" A0): the card's PCP is offered for confirmation,
          //   never saved as the PCP by itself (the D497 rule, check-pcp-profile).
          pcpFromCard: cardPcpFromScan(extracted) || s.insurance.pcpFromCard || null,
        },
      }
      return next
    })
  },

  // ----- auto-fill (post-extraction) -----
  // TEST 3.03 + TEST 3.04 (client half). Each document ADDS to the lists (it used to
  // replace them): an allergy whose name (trimmed, case-insensitive) is already listed is
  // dropped and the FIRST entry kept (Q40). A medication is a duplicate only when its
  // name, dosage AND frequency all match (normalised as noKnownItems.js does), within one
  // document or across documents: "Metformin 500 mg" and "Metformin 1000 mg" are two
  // lines, an exact repeat keeps the first (review round 1, MAJOR). An NKA / "none" phrase is
  // never an entry: it becomes the section's flag, and only when no real entry survives,
  // so "no known allergies" and a real allergy can never be saved together. Ids carry the
  // document's sequence number, so a second document's ids cannot collide with the first's.
  applyAutofillResult: (result, fileName) => {
    if (!result) return
    set((s) => {
      const docSeq = (s.autofillDocuments || []).length
      const next = {
        autofillResult: result,
        autofillSkipped: false,
        autofillDocuments: [...(s.autofillDocuments || []), { id: `doc-${Date.now()}-${docSeq}`, name: fileName || null }],
      }
      const dedupeKey = (name) => String(name || '').trim().toLowerCase()

      // Allergies — extracted as { substance, reaction, severity? }.
      // We default type to 'medication' (most common in records); patient
      // can change in the edit sheet.
      {
        const extracted = Array.isArray(result.allergies) ? result.allergies : []
        // ★ Q77 (Alex, 2026-09-30 late): a same-substance allergy from a later document keeps BOTH
        //   reactions on the one allergy ("Rash; Anaphylaxis"); an empty or 'Unknown' severity
        //   takes the later document's. Copies, so the earlier state is not mutated.
        const existing = (s.allergies?.items || []).map((a) => ({ ...a }))
        const byKey = new Map(existing.map((a) => [dedupeKey(a.name), a]))
        const seen = new Set(byKey.keys())
        // ★★★ PROD-473 (patient 242.229.0): a removed allergy stays removed when a later record says
        //   nothing new about it (a re-read of the same record), and comes back when it brings a new
        //   reaction or a rise to Severe (Q6) — review r2's X2/X3/X6 anaphylaxis is shown. It is read
        //   only for a substance that is NOT listed: a listed one (re-added by hand, S4h) takes the
        //   Q77 merge below.
        const removedByHand = s.autofillSuppressed || {}
        let sawNka = result.noKnownAllergies === true
        let merged = false
        const added = []
        extracted.forEach((a, i) => {
          const name = a.substance || a.name || ''
          if (isNkaAllergyPhrase(name)) { sawNka = true; return }
          const key = dedupeKey(name)
          if (!key) return
          if (seen.has(key)) {
            const hit = byKey.get(key)
            if (hit) {
              const have = String(hit.reaction || '').split(';').map((x) => x.trim()).filter(Boolean)
              const more = String(a.reaction || '').split(';').map((x) => x.trim()).filter(Boolean)
                .filter((r) => !have.some((h) => h.toLowerCase() === r.toLowerCase()))
              const before = [hit.reaction, hit.severity]
              if (more.length) { hit.reaction = [...have, ...more].join('; '); merged = true }
              if ((!hit.severity || hit.severity === 'Unknown') && a.severity) { hit.severity = a.severity; merged = true }
              // PROD-1006 (Alex Q59): the merged allergy shows the HIGHEST severity of the entries, never a
              //   lower one (this replaces AF-14's "a later record never overwrites a set severity"; the
              //   reactions still keep both, Q77). Two known severities that differ leave it unconfirmed.
              const had = severityRank(hit.severity)
              const got = severityRank(a.severity)
              if (got > had) { hit.severity = a.severity; merged = true }
              if (had > 0 && got > 0 && had !== got) { hit.confirmed = false; merged = true }
              // ★ review r1 MINOR 3: what the patient confirmed is not what the record now says;
              //   the allergy goes back to unconfirmed, so the patient confirms the merged text.
              if (hit.reaction !== before[0] || hit.severity !== before[1]) hit.confirmed = false
            }
            return
          }
          if (allergyStillRemoved(removedByHand, { name, reaction: a.reaction, severity: a.severity })) return
          seen.add(key)
          const item = {
            id: `ai-allergy-${docSeq}-${i}`,
            name,
            type: 'medication',
            severity: a.severity || 'Unknown',
            reaction: a.reaction || '',
            source: 'AI',
            confirmed: false,
          }
          byKey.set(key, item)
          added.push(item)
        })
        if (added.length > 0 || sawNka || merged) {
          next.allergies = {
            ...s.allergies,
            items: [...existing, ...added],
            noKnown: existing.length + added.length === 0 && sawNka,
          }
        }
      }

      // Medications — Profile uses 'dosage', not 'dose'. We map the
      // extractor's 'dosage' field directly.
      {
        const extracted = Array.isArray(result.medications) ? result.medications : []
        const existing = s.medications?.items || []
        const medKey = (m) => [m.name, m.dosage, m.frequency].map(normalizeNkaPhrase).join('\u0000')
        const seen = new Set([...existing.map(medKey), ...((s.autofillSuppressed && s.autofillSuppressed.medications) || [])])
        let sawNone = result.noCurrentMedications === true
        const added = []
        extracted.forEach((m, i) => {
          const name = m.name || ''
          if (isNkaMedPhrase(name)) { sawNone = true; return }
          const item = {
            id: `ai-med-${docSeq}-${i}`,
            name,
            dosage: m.dosage || m.dose || '',
            frequency: m.frequency || '',
            purpose: m.purpose || m.indication || '',
            source: 'AI',
            confirmed: false,
          }
          if (!dedupeKey(name)) return
          const key = medKey(item)
          if (seen.has(key)) return
          seen.add(key)
          added.push(item)
        })
        if (added.length > 0 || sawNone) {
          next.medications = {
            ...s.medications,
            items: [...s.medications.items, ...added],
            none: existing.length + added.length === 0 && sawNone,
          }
        }
      }

      // Optional sections — pre-seed in Profile-compatible shape so the
      // ExpandableCard renders matching state.
      // ★ Review round 1: a later document MERGES into what earlier ones gave; it used to
      //   REPLACE it, so a second record with only an appendectomy wiped hypertension and
      //   diabetes, set a false noConditions, and cleared the smoking history. Keep-first
      //   (Q40): an earlier entry with the same normalised name stays and the later one is
      //   dropped (a family entry gains the relatives the later document names); a "none"
      //   flag is computed from the MERGED list; a non-empty social field is never overwritten
      //   and a later value only fills an empty one. A FIRST document produces exactly what
      //   it did before: there is nothing earlier to merge with.
      const optional = { ...s.optional }
      // ★ PROD-428 (c): '-' and '_' read as spaces, so "Hernia-repair" and "hernia repair" from two
      //   records are one surgery (their ids, ai-surgery-hernia-repair, collided).
      const histKey = histKeyOf
      const sup = s.autofillSuppressed || {}
      const mergeKeepFirst = (earlier, later, suppressedKeys = []) => {
        const seen = new Set([...earlier.map(histKey), ...suppressedKeys])
        return [...earlier, ...later.filter((x) => !seen.has(histKey(x)))]
      }
      // ★ review r2 of patient push 3a (MINOR, S2h): a REMOVED family condition is not blocked. The
      //   condition key alone (`sup.family`) blocked it for every relative: Diabetes (mother)
      //   removed, a later record's Diabetes (father) was dropped; the parent showed it. The removal
      //   is still recorded; only an UNTICKED relative (MINOR 1, `familyRelatives`) is applied.
      // ★★ PROD-473 family half: a removed condition's relatives are in `familyRelatives` too, and a
      //   condition that is no longer on the list comes back only for a relative not remembered
      //   there (measured at 713dc775: Diabetes (mother) removed, the same record re-read put
      //   Diabetes (mother) back). With no relative left it is not added.
      const mergeRelatives = (earlier, later) => {
        const out = earlier.map((x) => ({ ...x }))
        const byKey = new Map(out.map((x) => [histKey(x), x]))
        const untickedRel = new Set(sup.familyRelatives || [])
        later.forEach((x) => {
          const hit = byKey.get(histKey(x))
          if (!hit) {
            const had = FAMILY_RELATIVES.some((rel) => x[rel])
            const kept = { ...x }
            for (const rel of FAMILY_RELATIVES) if (kept[rel] && untickedRel.has(`${histKey(x)}\u0000${rel}`)) kept[rel] = false
            if (!had || FAMILY_RELATIVES.some((rel) => kept[rel])) out.push(kept)
            return
          }
          for (const rel of FAMILY_RELATIVES) if (x[rel] && !untickedRel.has(`${histKey(x)}\u0000${rel}`)) hit[rel] = true
        })
        return out
      }

      if (Array.isArray(result.medicalHistory) && result.medicalHistory.length > 0) {
        const conditions = []
        const surgeries = []
        result.medicalHistory.forEach((entry) => {
          // Profile's structure separates conditions and surgeries
          if (entry.type === 'surgery' || /surger|appendec|tonsil|hernia/i.test(entry.name || entry.condition || '')) {
            surgeries.push({
              id: `ai-surgery-${(entry.name || '').toLowerCase().replace(/\s+/g, '-') || surgeries.length}`,
              name: entry.name || entry.condition || '',
              year: entry.year || '',
              custom: true,
            })
          } else {
            const matched = matchMedicalCondition(entry.condition || entry.name)
            if (matched) conditions.push({ ...matched, year: entry.year || '' })
          }
        })
        const prevHistory = s.optional?.medicalHistory
        const mergedConditions = mergeKeepFirst(prevHistory?.conditions || [], conditions, sup.conditions)
        const mergedSurgeries = mergeKeepFirst(prevHistory?.surgeries || [], surgeries, sup.surgeries)
        optional.medicalHistory = {
          ...(prevHistory || {}),
          conditions: mergedConditions,
          surgeries: mergedSurgeries,
          noConditions: mergedConditions.length === 0,
          noSurgeries: mergedSurgeries.length === 0,
        }
      }

      if (Array.isArray(result.familyHistory) && result.familyHistory.length > 0) {
        // Each extracted entry: { condition, relation }
        // Profile keys: mother / father / sibling / grandparent.
        const conditionsByKey = new Map()
        const otherConditions = []

        result.familyHistory.forEach((entry) => {
          const matched = matchFamilyCondition(entry.condition)
          if (!matched) return
          // ★ PROD-426 (patient push 3a): whole words, grand-/great- first (utils/familyRelation.js).
          //   The substring chain this replaced filed a GRANDMOTHER's condition under the mother.
          // ★ PROD-473 NIT: every relative the entry names ("mother and father" ticks both).
          const relations = familyRelations(entry.relation)
          if (!relations.length) return

          const ticked = Object.fromEntries(FAMILY_RELATIVES.map((rel) => [rel, relations.includes(rel)]))
          if (matched.custom) {
            const existing = otherConditions.find((c) => c.id === matched.id)
            if (existing) for (const rel of relations) existing[rel] = true
            else otherConditions.push({ ...matched, ...ticked })
          } else {
            const existing = conditionsByKey.get(matched.id)
            if (existing) for (const rel of relations) existing[rel] = true
            else conditionsByKey.set(matched.id, { ...matched, ...ticked })
          }
        })

        const prevFamily = s.optional?.familyHistory
        const famConds = mergeRelatives(prevFamily?.conditions || [], Array.from(conditionsByKey.values()))
        const famOther = mergeRelatives(prevFamily?.otherConditions || [], otherConditions)
        optional.familyHistory = {
          ...(prevFamily || {}),
          conditions: famConds,
          otherConditions: famOther,
          unknownRelatives: prevFamily?.unknownRelatives || { mother: false, father: false, sibling: false },
          // ★ PROD-428 (b): a hand-ticked "None" goes off once the merged lists hold anything
          //   (custom family conditions included). Only a record that had the flag carries it.
          ...(prevFamily && 'noConditions' in prevFamily ? { noConditions: prevFamily.noConditions === true && famConds.length + famOther.length === 0 } : {}),
        }
      }

      if (result.socialHistory && typeof result.socialHistory === 'object') {
        const sh = result.socialHistory
        const incoming = {
          smoking: mapStatus(sh.tobacco),
          smokingFrequency: sh.tobaccoDetails || '',
          alcohol: mapStatus(sh.alcohol),
          alcoholFrequency: sh.alcoholDetails || '',
          cannabis: mapStatus(sh.cannabis),
          cannabisFrequency: sh.cannabisDetails || '',
          cannabisMethod: sh.cannabisMethod || '',
          exercise: '',
          occupation: sh.occupation || '',
        }
        const prevSocial = s.optional?.socialLifestyle
        const isEmpty = (v) => v == null || String(v).trim() === ''
        const docDate = recordDateOf(result)
        const sources = s.autofillHabits || {}
        let took
        let locked = {}
        if (!prevSocial) {
          optional.socialLifestyle = incoming
          took = SOCIAL_HABIT_UNITS.map(([st]) => st).filter((st) => !isEmpty(incoming[st]))
        } else {
          // R30 · PROD-428 (g): see mergeHabitPairs. A habit is locked when no record supplied
          //   its current pair (typed, or loaded from the profile), or the patient changed it since.
          const newer = {}
          for (const [st, details] of SOCIAL_HABIT_UNITS) {
            const src = sources[st]
            newer[st] = isNewerRecord(docDate, src ? src.date : null)
            locked[st] = !isEmpty(prevSocial[st]) && (!src || src.status !== prevSocial[st] ||
              details.some((d) => String(src.details[d] || '') !== String(prevSocial[d] || '')))
          }
          const out = mergeHabitPairs(prevSocial, incoming, SOCIAL_HABIT_UNITS, { newer, locked })
          const merged = out.merged
          took = out.took
          const inUnit = new Set(SOCIAL_HABIT_UNITS.flatMap(([st, details]) => [st, ...details]))
          for (const [k, v] of Object.entries(incoming)) if (!inUnit.has(k) && isEmpty(prevSocial[k])) merged[k] = v
          optional.socialLifestyle = merged
        }
        // The pair each record-supplied habit now holds: a habit this record supplied carries its
        //   date; one an older record only topped up keeps its own date (still record-supplied).
        const nextSources = { ...sources }
        const sl = optional.socialLifestyle
        for (const [st, details] of SOCIAL_HABIT_UNITS) {
          const pair = { status: sl[st], details: Object.fromEntries(details.map((d) => [d, sl[d] || ''])) }
          if (took.includes(st)) nextSources[st] = { ...pair, date: docDate }
          else if (sources[st] && !locked[st]) nextSources[st] = { ...pair, date: sources[st].date }
        }
        next.autofillHabits = nextSources
      }

      next.optional = optional
      return next
    })
  },

  markAutofillSkipped: () => set({ autofillSkipped: true, autofillResult: null }),

  // ----- submit -----
  /**
   * Build the payload for PUT /api/v1/profile. Every section emits
   * the exact shape ProfileScreen.jsx and its sub-screens expect to
   * read on round-trip.
   */
  buildPayload: (account) => {
    const s = get()
    // TEST 3.01: a locked Email / Mobile is the account's (contactForWire)
    const contact = contactForWire(s.identity, account)

    const payload = {
      identification: {
        firstName: s.identity.firstName,
        lastName: s.identity.lastName,
        dob: s.identity.dob,
        sex: s.identity.sex,
        zipCode: s.identity.zipCode,
        email: contact.email,
        streetAddress: s.identity.streetAddress,
        apt: s.identity.apt,
        city: s.identity.city,
        state: s.identity.state,
        idFront: _slimCard(s.identity.idFront),
        idBack: _slimCard(s.identity.idBack),
        completed: true,
      },

      insurance: {
        selfPay: s.insurance.selfPay,
        coverageType: s.insurance.selfPay ? 'self-pay' : (s.insurance.coverageType || ''),
        provider: s.insurance.provider,
        providerId: s.insurance.providerId,
        memberId: s.insurance.memberId,
        groupNumber: s.insurance.groupNumber,
        // v228.11.8 — carry subscriber relationship + info so the profile and
        // downstream eligibility/claims see who the policyholder is.
        subscriberRelationship: s.insurance.subscriberRelationship || 'Self',
        subscriberFirstName: s.insurance.subscriberFirstName || '',
        subscriberLastName: s.insurance.subscriberLastName || '',
        subscriberDOB: s.insurance.subscriberDOB || '',
        cardFront: _slimCard(s.insurance.cardFront),
        cardBack: _slimCard(s.insurance.cardBack),
        // Profile aliases — populated for backwards compat with downstream readers
        insurerName: s.insurance.provider,
        planType: s.insurance.coverageType || (s.insurance.selfPay ? 'self-pay' : ''),
        // v228.2.0 — carry the inline Stedi results so the final submit doesn't
        // overwrite what the discovery/eligibility endpoints already persisted.
        ...(s.insurance.discovery_result ? { discovery_result: s.insurance.discovery_result } : {}),
        ...(s.insurance.last_eligibility ? { last_eligibility: s.insurance.last_eligibility } : {}),
        // ★ PROD-187 (P12): the payer keys the store holds (none on a clean store).
        ...payerKeysForWire(s.insurance),
        // s106 A0: the PCP the patient confirmed (or picked) on the insurance step, and the card's.
        ...(s.insurance.pcp ? { pcp: s.insurance.pcp } : {}),
        ...(s.insurance.pcpFromCard ? { pcpFromCard: s.insurance.pcpFromCard } : {}),
        completed: true,
      },

      // Allergies: split flat list into Profile's medList/foodList/contactList.
      allergies: (() => {
        const items = s.allergies.items || []
        const medList = items.filter((a) => a.type === 'medication').map((a) => ({
          id: a.id, name: a.name, severity: a.severity, reaction: a.reaction, custom: !!a.custom,
        }))
        const foodList = items.filter((a) => a.type === 'food').map((a) => ({
          id: a.id, name: a.name, severity: a.severity, reaction: a.reaction, custom: !!a.custom,
        }))
        const contactList = items.filter((a) => a.type === 'contact').map((a) => ({
          id: a.id, name: a.name, severity: a.severity, reaction: a.reaction, custom: !!a.custom,
        }))
        return {
          medList,
          foodList,
          contactList,
          hasMedAllergies: s.allergies.noKnown ? false : (medList.length > 0 ? true : null),
          hasFoodAllergies: s.allergies.noKnown ? false : (foodList.length > 0 ? true : null),
          hasContactAllergies: s.allergies.noKnown ? false : (contactList.length > 0 ? true : null),
          noKnownAllergies: s.allergies.noKnown,
          // Unified `list` for cross-screen reads
          list: [...medList, ...foodList, ...contactList],
          completed: true,
        }
      })(),

      medications: {
        // Profile uses `dosage`, not `dose`.
        list: s.medications.items.map((m) => ({
          name: m.name,
          dosage: m.dosage || '',
          frequency: m.frequency || '',
          purpose: m.purpose || '',
        })),
        hasMedications: s.medications.none ? false : (s.medications.items.length > 0 ? true : null),
        noMedications: s.medications.none,
        completed: true,
      },

      onboardingCompleted: true,
    }

    // Optional sections — only write if patient touched them (avoids
    // overwriting future Profile edits with empty defaults).
    if (s.optional.medicalHistory) {
      payload.medicalHistory = {
        conditions: s.optional.medicalHistory.conditions || [],
        surgeries: s.optional.medicalHistory.surgeries || [],
        noConditions: !!s.optional.medicalHistory.noConditions,
        noSurgeries: !!s.optional.medicalHistory.noSurgeries,
        completed: true,
      }
    }
    if (s.optional.familyHistory) {
      payload.familyHistory = {
        conditions: s.optional.familyHistory.conditions || [],
        otherConditions: s.optional.familyHistory.otherConditions || [],
        unknownRelatives: s.optional.familyHistory.unknownRelatives || { mother: false, father: false, sibling: false },
        completed: true,
      }
    }
    if (s.optional.socialLifestyle) {
      payload.socialLifestyle = {
        smoking: s.optional.socialLifestyle.smoking || '',
        smokingFrequency: s.optional.socialLifestyle.smokingFrequency || '',
        alcohol: s.optional.socialLifestyle.alcohol || '',
        alcoholFrequency: s.optional.socialLifestyle.alcoholFrequency || '',
        cannabis: s.optional.socialLifestyle.cannabis || '',
        cannabisFrequency: s.optional.socialLifestyle.cannabisFrequency || '',
        cannabisMethod: s.optional.socialLifestyle.cannabisMethod || '',
        exercise: s.optional.socialLifestyle.exercise || '',
        occupation: s.optional.socialLifestyle.occupation || '',
        completed: true,
      }
    }

    return payload
  },

  // v228.10.0: per-section checkpoint patch. Returns ONLY the slice for `step`
  // plus the onboarding_step marker (= next step). It intentionally omits
  // onboardingCompleted and the per-section `completed:true` flags, so saving a
  // checkpoint never marks an un-reached section as finished. Step map:
  //   1 = post-2FA basics flush, 2 = identity, 3 = insurance,
  //   4 = autofill (identity), 5 = allergies, 6 = medications.
  // (Step 7 finishes the flow via buildPayload, which sets onboardingCompleted.)
  buildSectionPatch: (step, account, { complete } = {}) => {
    const s = get()
    // TEST 3.01: a locked Email / Mobile is the account's (contactForWire)
    const contact = contactForWire(s.identity, account)
    const idBasics = {
      firstName: s.identity.firstName, lastName: s.identity.lastName,
      dob: s.identity.dob, sex: s.identity.sex, zipCode: s.identity.zipCode,
      email: contact.email,
    }
    const idFull = {
      ...idBasics,
      streetAddress: s.identity.streetAddress, apt: s.identity.apt,
      city: s.identity.city, state: s.identity.state,
      idFront: _slimCard(s.identity.idFront), idBack: _slimCard(s.identity.idBack),
    }
    const insuranceSlice = {
      selfPay: s.insurance.selfPay,
      coverageType: s.insurance.selfPay ? 'self-pay' : (s.insurance.coverageType || ''),
      provider: s.insurance.provider, providerId: s.insurance.providerId,
      memberId: s.insurance.memberId, groupNumber: s.insurance.groupNumber,
      cardFront: _slimCard(s.insurance.cardFront), cardBack: _slimCard(s.insurance.cardBack),
      insurerName: s.insurance.provider,
      planType: s.insurance.coverageType || (s.insurance.selfPay ? 'self-pay' : ''),
      ...(s.insurance.discovery_result ? { discovery_result: s.insurance.discovery_result } : {}),
      ...(s.insurance.last_eligibility ? { last_eligibility: s.insurance.last_eligibility } : {}),
      // ★ PROD-187 (P12): every payer key the store holds, '' / false / [] included. The
      //   truthy guards here dropped a cleared payerId, so a typed re-pick never reached the
      //   server and the old payer's id stayed. A clean store holds none and sends none.
      ...payerKeysForWire(s.insurance),
      // ★ PROD-822 (review REVIEW_PNEXT2 A-1): the policyholder, so the section save's eligibility
      //   check asks the payer about the policyholder its key names (eligibilityKey carries it for a
      //   dependent). Measured on 52166fb6 (review proof P1): the patch carried no subscriber key,
      //   so the check after typed details asked with the old (empty) ones. Never an empty name or
      //   DOB over a saved one (policyholderForWire).
      ...policyholderForWire(s.insurance),
    }
    const allergiesSlice = (() => {
      const items = s.allergies.items || []
      const pick = (t) => items.filter((a) => a.type === t).map((a) => ({ id: a.id, name: a.name, severity: a.severity, reaction: a.reaction, custom: !!a.custom }))
      const medList = pick('medication'), foodList = pick('food'), contactList = pick('contact')
      return {
        medList, foodList, contactList,
        hasMedAllergies: s.allergies.noKnown ? false : (medList.length > 0 ? true : null),
        hasFoodAllergies: s.allergies.noKnown ? false : (foodList.length > 0 ? true : null),
        hasContactAllergies: s.allergies.noKnown ? false : (contactList.length > 0 ? true : null),
        noKnownAllergies: s.allergies.noKnown,
        list: [...medList, ...foodList, ...contactList],
      }
    })()
    const medsSlice = {
      list: s.medications.items.map((m) => ({ name: m.name, dosage: m.dosage || '', frequency: m.frequency || '', purpose: m.purpose || '' })),
      hasMedications: s.medications.none ? false : (s.medications.items.length > 0 ? true : null),
      noMedications: s.medications.none,
    }
    const map = {
      1: { identification: idBasics },
      2: { identification: idFull },
      3: { insurance: insuranceSlice },
      4: { identification: idFull },
      5: { allergies: allergiesSlice },
      6: { medications: medsSlice },
    }
    const slice = map[step] || {}
    // ★ s95: a section the patient FINISHED on its own screen (the optional health-profile
    //   run after sign-up) is marked complete when it is saved. Sign-up no longer ends with
    //   buildPayload, which used to stamp insurance / allergies / medications complete
    //   whether or not they had been answered — that would wave a case through the
    //   before-share check with nothing on file.
    // ★ PROD-1132: `complete: false` (explicit) writes `completed:false` — "I'll add my card later" leaves
    //   the section unanswered even if an earlier save had stamped it. Left out (undefined) writes no flag.
    if (complete === true || complete === false) {
      for (const k of Object.keys(slice)) slice[k] = { ...slice[k], completed: complete }
    }
    return { ...slice, onboardingStep: Math.min((step || 0) + 1, TOTAL_STEPS - 1) }
  },

  // ★ s95: what finishing SIGN-UP writes — identification (complete: the About-you screen
  //   requires every field) and the onboardingCompleted flag. Nothing about insurance,
  //   allergies or medications: those are asked later, and only an answered section may
  //   be marked complete.
  buildEssentialsPayload: (account) => {
    // The identification half of buildPayload (one builder, one place the locked Email /
    // Mobile rule lives — contactForWire), and nothing else.
    const { identification } = get().buildPayload(account)
    return { identification, onboardingCompleted: true, onboardingStep: TOTAL_STEPS }
  },

  // v228.10.0: hydrate the store from a saved profile so a resumed onboarding
  // shows what the patient already entered (inverse of buildPayload's mapping).
  hydrateFromProfile: (profile) => {
    if (!profile || typeof profile !== 'object') return
    set((s) => {
      const next = {}
      const id = profile.identification
      if (id && typeof id === 'object') {
        next.identity = { ...s.identity }
        ;['firstName', 'lastName', 'dob', 'sex', 'zipCode', 'email', 'phone', 'streetAddress', 'apt', 'city', 'state', 'idFront', 'idBack'].forEach((k) => {
          if (id[k] !== undefined && id[k] !== null && id[k] !== '') next.identity[k] = id[k]
        })
      }
      const ins = profile.insurance
      if (ins && typeof ins === 'object') {
        next.insurance = { ...s.insurance }
        // PROD-431 (1), patient push 3a: every payer key (PAYER_KEYS), not two of the four id keys
        ;[...new Set(['selfPay', 'coverageType', 'provider', 'providerId', 'memberId', 'groupNumber', 'cardFront', 'cardBack', 'discovery_result', 'last_eligibility', 'payerId', 'stediPayerId', 'payerName', 'stediPayerName', 'payerMatchConfidence', 'payerMatchSource', 'payerNeedsConfirmation', 'payerCandidates', ...PAYER_KEYS])].forEach((k) => {
          if (ins[k] !== undefined && ins[k] !== null) next.insurance[k] = ins[k]
        })
        // ★ PROD-822 (review REVIEW_PNEXT2 A-1): the policyholder on file, so the step's key, its
        //   eligibility PUT and the section save agree with the saved row. Not loaded before (only
        //   strings that hold something), so a resumed dependent's policy was keyed and written
        //   with empty details (review proof P2a: a second check of the same question; P2b: the
        //   saved name and DOB blanked). Visible: the relationship arrives answered on a resume.
        ;['subscriberRelationship', 'subscriberFirstName', 'subscriberLastName', 'subscriberDOB'].forEach((k) => {
          if (typeof ins[k] === 'string' && ins[k].trim() !== '') next.insurance[k] = ins[k]
        })
        // ★ round 2 B-r2-1: the DOB as ISO (isoDob), read from subscriberDOB, subscriberDob or
        //   subscriber_dob. The md discovery Apply stores the payer's raw YYYYMMDD; ISO-only
        //   isFullDob then held policyholderPending true and onboarding never checked (no message).
        //   A value isoDob cannot read stays as stored, so the save sends it back unchanged.
        const rawDob = [ins.subscriberDOB, ins.subscriberDob, ins.subscriber_dob].find((v) => typeof v === 'string' && v.trim() !== '')
        if (rawDob) next.insurance.subscriberDOB = isoDob(rawDob) || rawDob
      }
      const al = profile.allergies
      if (al && typeof al === 'object') {
        const items = []
        const add = (list, type) => (list || []).forEach((a) => items.push({ id: a.id || `${type}-${a.name}`, name: a.name, type, severity: a.severity, reaction: a.reaction, custom: !!a.custom, confirmed: true }))
        add(al.medList, 'medication'); add(al.foodList, 'food'); add(al.contactList, 'contact')
        next.allergies = { ...s.allergies, items: items.length ? items : s.allergies.items, noKnown: !!al.noKnownAllergies }
      }
      const md = profile.medications
      if (md && typeof md === 'object') {
        const items = (md.list || []).map((m, i) => ({ id: m.id || `med-${i}`, name: m.name, dosage: m.dosage || '', frequency: m.frequency || '', purpose: m.purpose || '', confirmed: true }))
        next.medications = { ...s.medications, items: items.length ? items : s.medications.items, none: !!md.noMedications }
      }
      return next
    })
  },

  setSubmitting: (val) => set({ isSubmitting: val }),
  setSubmitError: (err) => set({ submitError: err }),

  reset: () => set({
    currentStep: 0,
    carouselSeen: false,
    autofillResult: null,
    autofillSkipped: false,
    autofillDocuments: [],
    autofillSuppressed: {},
    autofillHabits: {},
    identity: { ...initialIdentity },
    insurance: { ...initialInsurance },
    allergies: { ...initialAllergies },
    medications: { ...initialMedications },
    optional: { ...initialOptional },
    isSubmitting: false,
    submitError: null,
    eligibilityCheckedKey: null,
    eligibilityResultKey: null,
    eligibilityRun: null,
  }),
}))

// Re-export helpers for screens that need them (carrier matching during scan)
export { matchCarrier, matchMedicalCondition, matchFamilyCondition }
